import { readAppLanguage, localizeSystemError } from './i18n';
import { confirmationTimeFields } from './utils/confirmationTime';
import { localize } from './i18n';
import { recordPrn } from './utils/prnRecording';
import { clearMedicationCaches } from './utils/localCache';
import { journeyHeaders, clearJourneyIdentity, registrationEvent } from './utils/productJourneys';
import { productRelease } from './utils/productRelease';
import { analyticsId, clearProductEvents } from './utils/productAnalytics';
import axios from 'axios';

import { finishAccountDeletion } from './utils/accountDeletion';
import { reconcileNativeInstallation } from './utils/nativeInstallation';

// 전역 타임아웃 — 네트워크 장애 시 무한 대기 방지
axios.defaults.timeout = 15000;

const API_BASE = '/api/pill-reminder';

axios.interceptors.request.use(config => {
    if ((config.url || '').startsWith('/api/pill-reminder/devices/')) {
        const release = productRelease();
        config.headers = config.headers || {};
        config.headers['X-Product-Web-Release'] = release.webRelease;
        config.headers['X-Product-Native-Update'] = release.nativeUpdate;
        try { Object.assign(config.headers, journeyHeaders(config.url)); } catch {}
    }
    if (config.method?.toLowerCase() === 'post' && /\/logs(?:\/\d+)?\/confirm$/.test(config.url || '')) {
        config.headers = config.headers || {};
        config.headers['X-Product-Action-Id'] = config.headers['X-Product-Action-Id'] || analyticsId();
    }
    return config;
});

// Refresh the authoritative native schedule after successful dose/schedule changes.
// Never build a cancellation list from the home page's filtered cache.
axios.interceptors.response.use(response => {
    const { method, url = '' } = response.config || {};
    if (method !== 'get' && /\/account(?:\/|$)/.test(url) && !url.includes('/email-code') && !response.data?.dataChoice) { clearProductEvents(); clearJourneyIdentity(); }
    if (method && method.toLowerCase() !== 'get' && url.startsWith(`${API_BASE}/devices/`)
        && !response.data?.dataChoice && !url.endsWith('/account/delete') && !url.includes('/account/email-code')
        && /\/(medications|logs|settings|pause|resume|global-pause|account)(\/|$|\?)/.test(url)) {
        try { window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'REFRESH_MEDICATION_ALARMS' })); } catch {}
        window.dispatchEvent(new Event('medications-updated'));
    }
    if (method === 'delete' && url.endsWith('/data')) {
        try { window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'RESET_MEDICATION_ALARMS' })); } catch {}
    }
    return response;
}, error => {
    if (error.response?.status === 410 && (error.response.data?.code === 'ACCOUNT_DELETED' || error.response.data?.message === 'ACCOUNT_DELETED')) {
        void finishAccountDeletion();
    }
    // Keep structured codes/status/validation results intact. Korean users receive
    // the original error object and text; overseas UI must not leak untranslated diagnostics.
    const url = error.config?.url || error.response?.config?.url || '';
    if (readAppLanguage() !== 'ko' && url.startsWith(API_BASE) && error.response?.data) {
        const data = error.response.data;
        if (typeof data === 'object') {
            const copy = { ...data };
            for (const field of ['message', 'error']) {
                if (typeof copy[field] === 'string') copy[field] = localizeSystemError(copy[field]);
            }
            error.response.data = copy;
        }
    }
    return Promise.reject(error);
});

// UUID management
const UUID_KEY = 'pill_reminder_uuid';

export function getDeviceUuid() {
    reconcileNativeInstallation(window.NATIVE_INSTALLATION_ID);
    let uuid = localStorage.getItem(UUID_KEY);
    if (!uuid) {
        uuid = generateUUID();
        localStorage.setItem(UUID_KEY, uuid);
    }
    return uuid;
}

function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Device
export async function registerDevice(uuid, pushToken, platform, fcmToken, deviceModel, appVersion, runtimeInfo = {}) {
    const res = await axios.post(`${API_BASE}/devices`, {
        uuid,
        pushToken,
        platform,
        fcmToken,
        deviceModel,
        appVersion,
        runtimeVersion: runtimeInfo.runtimeVersion,
        channel: runtimeInfo.channel,
        osVersion: runtimeInfo.osVersion,
        store: runtimeInfo.store,
        locale: runtimeInfo.locale,
        timeZone: runtimeInfo.timeZone,
    });
    return res.data;
}

export async function updatePushToken(uuid, pushToken, platform, fcmToken, deviceModel, appVersion, runtimeInfo = {}) {
    const res = await axios.put(`${API_BASE}/devices/${uuid}/push-token`, {
        pushToken,
        fcmToken,
        platform,
        deviceModel,
        appVersion,
        runtimeVersion: runtimeInfo.runtimeVersion,
        channel: runtimeInfo.channel,
        osVersion: runtimeInfo.osVersion,
        store: runtimeInfo.store,
        locale: runtimeInfo.locale,
        timeZone: runtimeInfo.timeZone,
    });
    return res.data;
}

export async function getDeviceInfo(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/info`);
    return res.data;
}

export async function getRegistrationContext(uuid) {
    return (await axios.get(`${API_BASE}/devices/${uuid}/registration-context`)).data;
}
export async function getRegistrationResult(uuid, requestId, expectedContext) {
    return (await axios.get(`${API_BASE}/devices/${uuid}/medications/registration-result/${encodeURIComponent(requestId)}`, {
        params: { expectedContext },
    })).data;
}

export async function recordAdEvent(uuid, data) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/ad-events`, data);
    return res.data;
}

export async function getEntitlements(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/entitlements`);
    return res.data;
}

export async function markWidgetEnabled(uuid) {
    await axios.put(`${API_BASE}/devices/${uuid}/widget-enabled`);
}

export async function recordPageDuration(uuid, data) {
    await axios.post(`${API_BASE}/devices/${uuid}/page-duration`, data);
}

export async function getAccount(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/account`);
    return res.data;
}

export async function loginAccount(uuid, data) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/account/login`, data);
    return res.data;
}

export async function sendEmailLoginCode(uuid, email) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/account/email-code`, { email });
    return res.data;
}

export async function verifyEmailLoginCode(uuid, email, code) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/account/email-code/verify`, { email, code });
    return res.data;
}

export async function logoutAccount(uuid) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/account`);
    return res.data;
}

export async function deleteAccount(uuid, proof) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/account/delete`, proof, { timeout: 30000 });
    return res.data;
}

// Medications

export async function createMedicationsBulk(uuid, medications, options = {}) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/medications/bulk`, {
        medications,
        requestId: options.requestId || null,
        expectedContext: options.expectedContext || null,
    });
    return res.data;
}

export async function getMedications(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/medications`);
    return res.data;
}

export async function getMedicationLimit(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/medications/limit-info`);
    return res.data;
}

export async function claimMedicationSlotRewardBonus(uuid) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/medications/slot-reward-bonus`);
    return res.data;
}

export async function getRewardVerificationPolicy(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/reward-tickets/policy`, { timeout: 5000 });
    return res.data;
}

export async function getMedication(uuid, id) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/medications/${id}`);
    return res.data;
}

export async function updateMedication(uuid, id, data) {
    const res = await axios.put(`${API_BASE}/devices/${uuid}/medications/${id}`, data);
    return res.data;
}

export async function updateAllMedicationAlertMethod(uuid, data) {
    const res = await axios.patch(`${API_BASE}/devices/${uuid}/medications/alert-method`, data);
    return res.data;
}

export async function deleteMedication(uuid, id) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/medications/${id}`);
    return res.data;
}

export async function analyzePrescriptionImage(uuid, fileOrBlob, precision = false, consentVersion) {
    if (consentVersion !== 'photo-analysis-v1') throw new Error(localize('사진 분석 전송 동의가 필요해요'));
    const formData = new FormData();
    formData.append('image', fileOrBlob, 'prescription.jpg');
    const res = await axios.post(
        `${API_BASE}/devices/${uuid}/medication-bundles/analyze-image`,
        formData,
        {
            params: { precision, consentVersion },
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
        },
    );
    return res.data;
}

export async function analyzeMedicationNames(uuid, medications, options = {}) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/medication-bundles/analyze-names`, {
        medications,
        imageAnalysisToken: options.imageAnalysisToken || null,
        locale: readAppLanguage(),
    }, { timeout: 60000 });
    return res.data;
}

export async function checkMedicationBundleInteractions(uuid, medications) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/medication-bundles/check-interactions`, {
        medications,
        ...(readAppLanguage() === 'ko' ? {} : { locale: readAppLanguage() }),
    }, { timeout: 120000 });
    return res.data;
}

// Today
export async function getToday(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/today`);
    return res.data;
}

// Logs
export async function confirmLog(uuid, logId, confirmedAt = null) {
    const body = confirmedAt ? confirmationTimeFields(confirmedAt) : undefined;
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/${logId}/confirm`, body);
    return res.data;
}

export async function confirmLogs(uuid, logIds, confirmedAt = null) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/confirm`, { logIds, ...confirmationTimeFields(confirmedAt) });
    return res.data;
}

export async function unconfirmLog(uuid, logId) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/${logId}/unconfirm`);
    return res.data;
}

export async function unconfirmLogs(uuid, logIds) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/unconfirm`, { logIds });
    return res.data;
}

export async function skipLog(uuid, logId, reason) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/${logId}/skip`, { reason });
    return res.data;
}

export async function skipLogs(uuid, logIds, reason) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/skip`, { logIds, reason });
    return res.data;
}

export async function deferLog(uuid, logId, deferUntil) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/${logId}/defer`, { deferUntil });
    return res.data;
}

export async function deferLogs(uuid, logIds, deferUntil) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/logs/defer`, { logIds, deferUntil });
    return res.data;
}

export async function backfillLog(uuid, medicationId, date, status) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/medications/${medicationId}/backfill-log`, { date, status });
    return res.data;
}

export async function takeAsNeeded(uuid, medicationId, takenAt = null) {
    return recordPrn(async body => {
        const res = await axios.post(`${API_BASE}/devices/${uuid}/medications/${medicationId}/take-now`, body);
        clearMedicationCaches();
        return res.data;
    }, uuid, medicationId, takenAt);
}

export async function deleteAsNeededLog(uuid, logId) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/logs/${logId}`);
    return res.data;
}

export async function deleteLog(uuid, logId) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/logs/${logId}`);
    return res.data;
}

export async function updateAsNeededLogTakenAt(uuid, logId, takenAt) {
    const res = await axios.patch(`${API_BASE}/devices/${uuid}/logs/${logId}/taken-at`, { takenAt });
    return res.data;
}

export async function getPrnLogsByDate(uuid, date = null) {
    const params = date ? { date } : {};
    const res = await axios.get(`${API_BASE}/devices/${uuid}/prn-logs`, { params });
    return res.data;
}

export async function updateConfirmedAt(uuid, logId, confirmedAt) {
    const res = await axios.patch(`${API_BASE}/devices/${uuid}/logs/${logId}/confirmed-at`, { confirmedAt });
    return res.data;
}

export async function getConfirmationWindow(logIds) {
    if (!Array.isArray(logIds) || !logIds.length || logIds.some(id => id == null || String(id).trim() === '')) {
        throw new Error(localize('처리할 복용 기록이 없어요'));
    }
    const res = await axios.get(`${API_BASE}/devices/${getDeviceUuid()}/logs/confirmation-window`, {
        params: { logIds: logIds.join(',') },
    });
    return res.data;
}

export async function updateConfirmedAtLogs(uuid, logIds, confirmedAt) {
    const res = await axios.patch(`${API_BASE}/devices/${uuid}/logs/confirmed-at`, { logIds, confirmedAt });
    return res.data;
}

export async function getCalendar(uuid, month) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/logs`, { params: { month } });
    return res.data;
}

export async function getCalendarView(uuid, month) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/calendar`, { params: { month } });
    return res.data;
}

export async function getStreak(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/streak`);
    return res.data;
}

export async function getCharacter(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/character`);
    return res.data;
}

// Settings
export async function getSettings(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/settings`);
    return res.data;
}

export async function updateSettings(uuid, data) {
    const res = await axios.put(`${API_BASE}/devices/${uuid}/settings`, data);
    return res.data;
}

// AI Chat
export async function sendChatMessage(uuid, message, history = []) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/chat`, { message, history, locale: readAppLanguage() }, { timeout: 60000 });
    return res.data;
}

export async function claimChatRewardBonus(uuid) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/chat/reward-bonus`);
    return res.data;
}


export async function reportChatMessage(uuid, messageContent, reason = null) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/chat/report`, { messageContent, reason });
    return res.data;
}

// App Store 5.1.1(v) — 사용자 데이터 영구 삭제
export async function deleteAllMyData(uuid) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/data`);
    return res.data;
}

// Stats
export async function getStats(uuid, startDate, endDate) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/stats`, { params: { startDate, endDate } });
    return res.data;
}

export async function getMonthlyRecap(uuid, year, month) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/monthly-recap`, { params: { year, month } });
    return res.data;
}

// Symptoms
export async function createSymptom(uuid, data) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/symptoms`, data);
    return res.data;
}

export async function getSymptoms(uuid, offsetOrParams = 0, limit = 50) {
    const params = typeof offsetOrParams === 'object'
        ? offsetOrParams
        : { offset: offsetOrParams, limit };
    const res = await axios.get(`${API_BASE}/devices/${uuid}/symptoms`, { params });
    return res.data;
}

export async function updateSymptom(uuid, id, data) {
    const res = await axios.put(`${API_BASE}/devices/${uuid}/symptoms/${id}`, data);
    return res.data;
}

export async function deleteSymptom(uuid, id) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/symptoms/${id}`);
    return res.data;
}

// Guardian
export async function getGuardianTargets(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/guardian/targets`);
    return res.data;
}

export async function getGuardianTargetCalendar(uuid, groupId, month) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/guardian/targets/${groupId}/calendar`, { params: { month } });
    return res.data;
}

export async function getGuardianTargetToday(uuid, groupId) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/guardian/targets/${groupId}/today`);
    return res.data;
}

// 보호자가 대상자의 약을 복용 처리. confirmedAt은 LocalDateTime ISO 문자열 (없으면 서버 now()).

export async function confirmGuardianLogs(uuid, groupId, logIds, confirmedAt = null) {
    const res = await axios.post(
        `${API_BASE}/devices/${uuid}/guardian/targets/${groupId}/logs/confirm`,
        confirmedAt ? { logIds, confirmedAt } : { logIds },
    );
    return res.data;
}

export async function sendGuardianNudge(uuid, groupId, logId, data) {
    const res = await axios.post(
        `${API_BASE}/devices/${uuid}/guardian/targets/${groupId}/logs/${logId}/nudge`,
        data,
    );
    return res.data;
}

export async function claimGuardianNudgeRewardBonus(uuid) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/guardian/nudge-reward-bonus`);
    return res.data;
}

export async function getMyGuardians(uuid, noCache = false) {
    const params = noCache ? { _t: Date.now() } : {};
    const res = await axios.get(`${API_BASE}/devices/${uuid}/guardian/my-guardians`, { params });
    return res.data;
}

export async function updateGuardianLinkNickname(uuid, linkId, nickname) {
    const res = await axios.patch(`${API_BASE}/devices/${uuid}/guardian/links/${linkId}/nickname`, { nickname });
    return res.data;
}

export async function updateGuardianTargetNickname(uuid, linkId, nickname) {
    const res = await axios.patch(`${API_BASE}/devices/${uuid}/guardian/links/${linkId}/target-nickname`, { nickname });
    return res.data;
}

export async function removeGuardianLink(uuid, linkId) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/guardian/links/${linkId}`);
    return res.data;
}

export async function getGuardianLinkSettings(uuid, linkId) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/guardian/links/${linkId}/settings`);
    return res.data;
}

export async function updateGuardianLinkSettings(uuid, linkId, data) {
    const res = await axios.put(`${API_BASE}/devices/${uuid}/guardian/links/${linkId}/settings`, data);
    return res.data;
}

// Review
export async function submitReview(uuid, data) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/review`, data);
    return res.data;
}

export async function getMyReviews(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/review`);
    return res.data;
}

export async function getReviewUnreadCount(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/review/unread-count`);
    return res.data;
}

export async function markReviewRead(uuid, reviewId) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/review/${reviewId}/read`);
    return res.data;
}

export async function recordStoreReviewCtaEvent(uuid, data) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/store-review-cta-events`, data);
    return res.data;
}

export async function getStoreReviewCtaEligibility(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/store-review-cta-eligibility`);
    return res.data;
}

export async function getAdminStoreReviewCtaStats(from, to, granularity = 'daily') {
    const res = await axios.get(`${API_BASE}/admin/store-review-cta`, {
        params: { from, to, granularity },
        headers: adminHeaders(),
    });
    return res.data;
}

export async function getAdminReviews(hidden = false) {
    const res = await axios.get(`${API_BASE}/admin/reviews`, {
        params: { hidden },
        headers: adminHeaders(),
    });
    return res.data;
}

export async function replyAdminReview(id, data) {
    const res = await axios.post(`${API_BASE}/admin/reviews/${id}/reply`, data, { headers: adminHeaders() });
    return res.data;
}

export async function hideAdminReview(id, hidden = true) {
    const res = await axios.patch(`${API_BASE}/admin/reviews/${id}/hidden`, { hidden }, { headers: adminHeaders() });
    return res.data;
}

// Device Group
export async function getGroupInfo(uuid) {
    const res = await axios.get(`${API_BASE}/devices/${uuid}/group`);
    return res.data;
}

export async function createInviteCode(uuid, type = 'SYNC') {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/group/invite`, { type });
    return res.data;
}

export async function joinByInviteCode(uuid, inviteCode) {
    const res = await axios.post(`${API_BASE}/devices/${uuid}/group/join`, { inviteCode });
    return res.data;
}

export async function disconnectGroupDevice(uuid, targetUuid) {
    const res = await axios.delete(`${API_BASE}/devices/${uuid}/group/devices/${targetUuid}`);
    return res.data;
}

// Medication Image
export async function uploadMedicationImage(uuid, medicationId, file, expectedImageUrl) {
    const formData = new FormData();
    formData.append('file', file);
    if (expectedImageUrl !== undefined) formData.append('expectedImageUrl', expectedImageUrl || '');
    clearMedicationCaches();
    try {
        const res = await axios.post(
            `${API_BASE}/devices/${uuid}/medications/${medicationId}/image`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return res.data;
    } finally {
        // Also invalidate when the response is lost: the server may already have committed.
        clearMedicationCaches();
    }
}

export async function deleteMedicationImage(uuid, medicationId, expectedImageUrl) {
    clearMedicationCaches();
    try {
        const res = await axios.delete(`${API_BASE}/devices/${uuid}/medications/${medicationId}/image`, {
            params: expectedImageUrl === undefined ? undefined : { expectedImageUrl: expectedImageUrl || '' },
        });
        return res.data;
    } finally { clearMedicationCaches(); }
}

// Medicine Search (공공데이터포털)
export async function searchMedicines(keyword, language) {
    registrationEvent('medicine_search', 'submit');
    try {
        const res = await axios.get(`${API_BASE}/medicines/search`, { params: { keyword, numOfRows: 10, language } });
        registrationEvent('medicine_search', 'complete');
        return res.data;
    } catch (error) { registrationEvent('medicine_search', 'error'); throw error; }
}


// Medication Suggestion (Smart Auto-fill)

// Drug Interaction Check (DUR)

// Admin Analytics
const ADMIN_PASSWORD_KEY = 'admin_password';
function adminHeaders() {
    const pw = sessionStorage.getItem(ADMIN_PASSWORD_KEY) || '';
    return { 'X-Admin-Password': pw };
}



export async function getAdminUsageSummary() {
    const res = await axios.get(`${API_BASE}/admin/stats/usage-summary`, { headers: adminHeaders() });
    return res.data;
}

export async function getAdminPlatformActivity(days = 7, includeToday = true) {
    const res = await axios.get(`${API_BASE}/admin/stats/active-platforms`, { params: { days, includeToday }, headers: adminHeaders() });
    return res.data;
}

export async function getAdminUsageTrend(from, to) {
    const res = await axios.get(`${API_BASE}/admin/stats/usage-trend`, { params: { from, to }, headers: adminHeaders() });
    return res.data;
}

export async function collectStats(date) {
    const res = await axios.post(`${API_BASE}/admin/stats/collect`, null, { params: { date }, headers: adminHeaders() });
    return res.data;
}

export async function getMedicationDistribution() {
    const res = await axios.get(`${API_BASE}/admin/stats/medication-distribution`, { headers: adminHeaders() });
    return res.data;
}

export async function getImageRegistrationDistribution() {
    const res = await axios.get(`${API_BASE}/admin/stats/image-registration-distribution`, { headers: adminHeaders() });
    return res.data;
}

export async function getNotificationPatterns() {
    const res = await axios.get(`${API_BASE}/admin/stats/notification-patterns`, { headers: adminHeaders() });
    return res.data;
}

export async function getTodayMedicationActions() {
    const res = await axios.get(`${API_BASE}/admin/stats/today-medication-actions`, { headers: adminHeaders() });
    return res.data;
}

export async function getAdminPageDurationStats(from, to) {
    const res = await axios.get(`${API_BASE}/admin/stats/page-duration`, { params: { from, to }, headers: adminHeaders() });
    return res.data;
}

export async function getAdminAdStats(from, to, limit = 0, granularity = 'daily') {
    const res = await axios.get(`${API_BASE}/admin/ads`, { params: { from, to, limit, granularity }, headers: adminHeaders() });
    return res.data;
}

export async function grantAdminAdFree(uuid, note = '', scope = 'GROUP') {
    const res = await axios.post(`${API_BASE}/admin/devices/${uuid}/entitlements/ad-free`, { note, scope }, { headers: adminHeaders() });
    return res.data;
}

export async function revokeAdminAdFree(uuid) {
    const res = await axios.delete(`${API_BASE}/admin/devices/${uuid}/entitlements/ad-free`, { headers: adminHeaders() });
    return res.data;
}

export async function getDeploymentInfo() {
    const res = await axios.get(`${API_BASE}/admin/deployment-info`, { headers: adminHeaders() });
    return res.data;
}

export async function getMedicineAliasCandidates(status = 'PENDING', limit = 20) {
    const params = { limit };
    if (status) params.status = status;
    const res = await axios.get(`${API_BASE}/admin/medicine-alias-candidates`, { params, headers: adminHeaders() });
    return res.data;
}

export async function approveMedicineAliasCandidate(id, data = {}) {
    const res = await axios.post(`${API_BASE}/admin/medicine-alias-candidates/${id}/approve`, data, { headers: adminHeaders() });
    return res.data;
}

export async function rejectMedicineAliasCandidate(id, reason = 'ADMIN_REJECTED') {
    const res = await axios.post(`${API_BASE}/admin/medicine-alias-candidates/${id}/reject`, { reason }, { headers: adminHeaders() });
    return res.data;
}

// Operations key is held in component memory only.
export async function adminOperations(key, action, body) {
    const headers = { ...adminHeaders(), 'X-Operations-Key': key };
    const url = `${API_BASE}/admin/operations${action ? `/${action}` : ''}`;
    const res = action
        ? await axios.post(url, body, { headers, timeout: 55000 })
        : await axios.get(url, { headers, timeout: 10000 });
    return res.data;
}

export async function getProductAnalytics(from, to, audience = 'customer', platform = 'all', version = 'all') {
    const res = await axios.get(`${API_BASE}/admin/stats/product`, { params: { from, to, audience, platform, version }, headers: adminHeaders() });
    return res.data;
}
export async function setAnalyticsAudience(uuid, audience) {
    await axios.put(`${API_BASE}/admin/stats/product/audience`, { uuid, audience }, { headers: adminHeaders() });
}

export async function getBusinessMetrics(month) {
    return (await axios.get(`${API_BASE}/admin/stats/business`, { params: { month }, headers: adminHeaders() })).data;
}
export async function saveBusinessMetric(entry) {
    await axios.put(`${API_BASE}/admin/stats/business`, entry, { headers: adminHeaders() });
}

export async function getAdminReviewAuthor(id) {
    const res = await axios.get(`${API_BASE}/admin/reviews/${id}/author`, { headers: adminHeaders() });
    return res.data;
}

export async function getAdminPurchaseCustomers(page = 0) {
    const res = await axios.get(`${API_BASE}/admin/purchase-customers`, { headers: adminHeaders(), params: { page } });
    return res.data;
}
export async function getAdminPurchaseCustomer(id) {
    const res = await axios.get(`${API_BASE}/admin/purchase-customers/${encodeURIComponent(id)}`, { headers: adminHeaders() });
    return res.data;
}
